import type { PaginatedExtendedGroupByQuery } from '@/api/generated/model';
import { QueryOrderingDirection } from '@/api/generated/model';
import type { Sort } from '@/comps/sortable-table-head';
import {
  COMPOSITION_UNCLASSIFIED_COLOR,
  COMPOSITION_UNCLASSIFIED_LABEL,
  COMPOSITION_UNCLASSIFIED_REMAINDER_MIN_SHARE,
  COMPOSITION_UNCLASSIFIED_SEGMENT_KEY,
  DISTRIBUTION_STRIP_COVERAGE,
  type SpendGroupKind,
} from '@/domains/pages/ai-spend/constants';
import { humanizeString } from '@/utils/string.utils';
import { translate } from '@/utils/translations';
import type { CompositionSegment } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { computeSpendPerTask } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { MULTIPLE_COLORS } from '@/constants/ui.constants';
import { formatNumber } from '@/utils/format.util';

export type GroupSpendRow = {
  key: string;
  name: string;
  cost: number;
  share: number;
  spendPerTask: number | null;
  tasks: number;
  users: number;
  failureRate: number | null;
  color: string;
};

export type SpendSortKey = 'name' | 'cost' | 'spendPerTask' | 'tasks' | 'users' | 'failureRate';

/** Matches report datatable breakdown labels: reserved slugs like `other` become title case. */
export function formatSpendGroupName(rawName: string, kind: SpendGroupKind): string {
  if (kind === 'tenure') return translate('tenure', rawName);
  if (kind !== 'task') return rawName;
  return humanizeString(rawName);
}

function computeFailureRate(roiSuccessRate: number | null | undefined): number | null {
  if (roiSuccessRate == null) return null;
  return 1 - roiSuccessRate;
}

export function mapGroupSpendRows(
  data: PaginatedExtendedGroupByQuery,
  totalCost: number,
  kind: SpendGroupKind,
): GroupSpendRow[] {
  const denominator = totalCost > 0 ? totalCost : 0;

  return data.data
    .map((row, index) => {
      const rawName = row.group_name?.trim() || 'Unknown';
      const name = formatSpendGroupName(rawName, kind);
      const cost = row.cost ?? 0;
      const tasks = row.n_segments ?? 0;

      return {
        key: rawName,
        name,
        cost,
        share: denominator > 0 ? cost / denominator : 0,
        spendPerTask: computeSpendPerTask(cost, tasks),
        tasks,
        users: row.n_users ?? 0,
        failureRate: computeFailureRate(row.roi_success_rate),
        color: MULTIPLE_COLORS[index % MULTIPLE_COLORS.length]!,
      };
    })
    .sort((left, right) => right.cost - left.cost);
}

export function buildCumulativeCompositionSegments(
  rows: GroupSpendRow[],
  options?: { coverage?: number; otherLabel?: string },
): CompositionSegment[] {
  const coverage = options?.coverage ?? DISTRIBUTION_STRIP_COVERAGE;
  const otherLabel = options?.otherLabel ?? 'Other';
  const visibleRows: GroupSpendRow[] = [];
  const tailRows: GroupSpendRow[] = [];
  let cumulativeShare = 0;
  let thresholdReached = false;

  for (const row of rows) {
    if (row.cost <= 0) continue;

    if (thresholdReached) {
      tailRows.push(row);
      continue;
    }

    visibleRows.push(row);
    cumulativeShare += row.share;

    if (cumulativeShare >= coverage) {
      thresholdReached = true;
    }
  }

  const segments: CompositionSegment[] = visibleRows.map((row) => ({
    key: row.key,
    label: row.name,
    value: row.cost,
    share: row.share,
    color: row.color,
  }));

  if (tailRows.length === 0) return segments;

  const otherValue = tailRows.reduce((sum, row) => sum + row.cost, 0);
  const otherShare = tailRows.reduce((sum, row) => sum + row.share, 0);

  segments.push({
    key: '__other__',
    label: `${otherLabel} (${formatNumber(tailRows.length)})`,
    value: otherValue,
    share: otherShare,
    color: '#71717A',
    isOther: true,
    otherCount: tailRows.length,
  });

  return segments;
}

const REMAINDER_COST_EPSILON = 1e-9;

export function computeCompositionRemainder(
  rows: GroupSpendRow[],
  totalCost: number,
): { cost: number; share: number } | null {
  if (totalCost <= 0) return null;

  const classifiedCost = rows.reduce((sum, row) => sum + row.cost, 0);
  const remainderCost = totalCost - classifiedCost;

  if (remainderCost <= REMAINDER_COST_EPSILON) return null;

  return {
    cost: remainderCost,
    share: remainderCost / totalCost,
  };
}

export function appendUnclassifiedRemainderSegment(
  segments: CompositionSegment[],
  remainder: { cost: number; share: number },
  options?: {
    minShare?: number;
    label?: string;
    color?: string;
  },
): CompositionSegment[] {
  const minShare = options?.minShare ?? COMPOSITION_UNCLASSIFIED_REMAINDER_MIN_SHARE;
  if (remainder.share < minShare) return segments;

  return [
    ...segments,
    {
      key: COMPOSITION_UNCLASSIFIED_SEGMENT_KEY,
      label: options?.label ?? COMPOSITION_UNCLASSIFIED_LABEL,
      value: remainder.cost,
      share: remainder.share,
      color: options?.color ?? COMPOSITION_UNCLASSIFIED_COLOR,
      isUnclassified: true,
    },
  ];
}

export function buildSpendCompositionSegments(
  rows: GroupSpendRow[],
  options: {
    otherLabel: string;
    kind: SpendGroupKind;
    totalCost: number | null;
  },
): CompositionSegment[] {
  const segments = buildCumulativeCompositionSegments(rows, { otherLabel: options.otherLabel });

  if (options.kind !== 'task' || options.totalCost == null) return segments;

  const remainder = computeCompositionRemainder(rows, options.totalCost);
  if (!remainder) return segments;

  return appendUnclassifiedRemainderSegment(segments, remainder);
}

function compareNullableNumbers(left: number | null, right: number | null, direction: QueryOrderingDirection): number {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;

  const delta = left - right;
  return direction === QueryOrderingDirection.asc ? delta : -delta;
}

export function sortGroupSpendRows(rows: GroupSpendRow[], sort: Sort<SpendSortKey> | undefined): GroupSpendRow[] {
  if (!sort) return rows;

  const sorted = [...rows];

  sorted.sort((left, right) => {
    switch (sort.by) {
      case 'name': {
        const delta = left.name.localeCompare(right.name);
        return sort.direction === QueryOrderingDirection.asc ? delta : -delta;
      }
      case 'cost':
        return compareNullableNumbers(left.cost, right.cost, sort.direction);
      case 'spendPerTask':
        return compareNullableNumbers(left.spendPerTask, right.spendPerTask, sort.direction);
      case 'tasks':
        return compareNullableNumbers(left.tasks, right.tasks, sort.direction);
      case 'users':
        return compareNullableNumbers(left.users, right.users, sort.direction);
      case 'failureRate':
        return compareNullableNumbers(left.failureRate, right.failureRate, sort.direction);
      default:
        return 0;
    }
  });

  return sorted;
}
